import { Component, ChangeDetectorRef, ViewEncapsulation, ViewChild, TemplateRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SAUTableModule } from '@some-angular-utils/table';
import { Urls } from '@/apiConnection/urls';
import { ApiService } from '@/apiConnection/ApiService';
import { Utils } from '@/utils/utils';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';

@Component({
  imports: [
    CommonModule,
    SAUTableModule,
    CentToEurDisplayDirective,
  ],
  templateUrl: './document-convert-to-budget.page.html',
  encapsulation: ViewEncapsulation.None,
})
export class DocumentConvertToBudgetPage implements OnInit {

  @ViewChild('sauTable')
  sauTable: { refresh: () => void } | undefined;

  @ViewChild('selectTmpl', { static: true }) selectTmpl!: TemplateRef<any>;
  @ViewChild('totalTmpl', { static: true }) totalTmpl!: TemplateRef<any>;

  public url = Urls.urls.documents;
  public converting = false;
  public selectedIds = new Set<number>();
  public currentPageItems: any[] = [];

  public headers = [
    { name: '', key: 'select' },
    { name: 'Nº Albarán', key: 'id_by_company' },
    { name: 'Cliente', key: 'client.name' },
    { name: 'Fecha', key: 'date', type: 'date' },
    { name: 'Total', key: 'total' },
  ];

  // unrelated_only/type are fixed, hidden filters (kept out of `order` so sau-table
  // never renders them as user-editable fields) — see DocumentsController::index()'s
  // `unrelated_only` param: document_ref null AND not referenced by any other document.
  public filterConfig: any = {
    orderParamName: 'sort',
    orderByFields: [
      { field: 'date', label: 'Fecha' },
      { field: 'total', label: 'Total' },
    ],
    order: ['dateRange', 'client'],
    mobile: ['dateRange'],
    form: {
      unrelated_only: {
        key: 'unrelated_only',
        type: 'inputText',
        defaultValue: true,
      },
      type: {
        key: 'types',
        type: 'inputText',
        defaultValue: 'albaran',
      },
      dateRange: {
        name: 'Periodo',
        key: 'date_start',
        keyTo: 'date_end',
        type: 'dateRange',
        defaultValue: [],
      },
      client: {
        name: 'Cliente',
        key: 'client_id',
        type: 'selectSimple',
        dropdowns: [],
      },
    },
  };

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.getDropdowns();
  }

  getDropdowns(): void {
    this.apiService.getSelectors(Urls.urls.selectors, { clients: {} }).subscribe({
      next: (data: any) => {
        this.filterConfig.form.client.dropdowns.push(...(data.clients || []));
        this.cdr.detectChanges();
      },
    });
  }

  onItemsLoaded(items: any[]): void {
    this.currentPageItems = items || [];
    // Drop selected ids that fell off the current result set (e.g. after a filter change)
    // is intentionally NOT done here — selection persists across pages/filters until convert/clear.
    this.cdr.detectChanges();
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  toggleSelect(id: number): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  get allVisibleSelected(): boolean {
    return this.currentPageItems.length > 0 && this.currentPageItems.every(item => this.selectedIds.has(item.id));
  }

  toggleSelectAllVisible(): void {
    if (this.allVisibleSelected) {
      this.currentPageItems.forEach(item => this.selectedIds.delete(item.id));
    } else {
      this.currentPageItems.forEach(item => this.selectedIds.add(item.id));
    }
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  convertSelected(): void {
    if (!this.selectedIds.size || this.converting) return;

    const ids = Array.from(this.selectedIds);
    this.converting = true;

    this.apiService.save(Urls.urls.documentsConvertToBudget, { ids }, 0).subscribe({
      next: (result: any) => {
        this.converting = false;
        const convertedCount = result?.converted?.length ?? 0;
        const skippedCount = result?.skipped?.length ?? 0;
        Utils.notification.success(
          skippedCount
            ? `${convertedCount} albarán(es) convertidos a presupuesto. ${skippedCount} ya no estaban disponibles y se han omitido.`
            : `${convertedCount} albarán(es) convertidos a presupuesto correctamente.`
        );
        this.clearSelection();
        this.sauTable?.refresh();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.converting = false;
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al convertir los albaranes a presupuesto');
        this.cdr.detectChanges();
      },
    });
  }
}
